import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { networksActions } from "./slices";
import {
  AddRouteParams,
  AddRouteResponse,
  AddRouterInterfaceParams,
  AddRouterInterfaceResponse,
  CreateFirewallParams,
  CreateFirewallResponse,
  CreateFirewallRuleParams,
  CreateFirewallRuleResponse,
  CreateFloatingIPParams,
  CreateFloatingIPResponse,
  CreateNetworkInterfaceParams,
  CreateNetworkInterfaceResponse,
  CreateNetworkParams,
  CreateNetworkResponse,
  CreateRouterParams,
  CreateRouterResponse,
  CreateSubnetParams,
  CreateSubnetResponse,
  DeleteFirewallParams,
  DeleteFirewallResponse,
  DeleteFirewallRuleParams,
  DeleteFirewallRuleResponse,
  DeleteFloatingIPParams,
  DeleteFloatingIPResponse,
  DeleteNetworkInterfaceParams,
  DeleteNetworkInterfaceResponse,
  DeleteNetworkParams,
  DeleteNetworkResponse,
  DeleteRouteParams,
  DeleteRouteResponse,
  DeleteRouterInterfaceParams,
  DeleteRouterInterfaceResponse,
  DeleteRouterParams,
  DeleteRouterResponse,
  DeleteSubnetParams,
  DeleteSubnetResponse,
  EditInterfaceFirewallsParams,
  EditInterfaceFirewallsResponse,
  EditNetworkInterfaceParams,
  EditNetworkInterfaceResponse,
  GetFirewallParams,
  GetFirewallResponse,
  GetFirewallRuleParams,
  GetFirewallRuleResponse,
  GetFirewallRulesParams,
  GetFirewallRulesResponse,
  GetFirewallsParams,
  GetFirewallsResponse,
  GetFloatingIPsParams,
  GetFloatingIPsResponse,
  GetInterfacesParams,
  GetInterfacesResponse,
  GetNetworkInterfacesParams,
  GetNetworkInterfacesResponse,
  GetNetworkLimitsParams,
  GetNetworkLimitsResponse,
  GetNetworkParams,
  GetNetworkResponse,
  GetNetworksParams,
  GetNetworksResponse,
  GetNetworkSubnetsParams,
  GetNetworkSubnetsResponse,
  GetRouterInterfacesParams,
  GetRouterInterfacesResponse,
  GetRouterParams,
  GetRouterResponse,
  GetRoutersParams,
  GetRoutersResponse,
  GetSubnetParams,
  GetSubnetResponse,
  GetSubnetsParams,
  GetSubnetsResponse,
  UpdateFirewallParams,
  UpdateFirewallResponse,
  UpdateFloatingIPParams,
  UpdateFloatingIPResponse,
  UpdateNetworkParams,
  UpdateNetworkResponse,
  UpdateRouterParams,
  UpdateRouterResponse,
  UpdateRouterStateParams,
  UpdateRouterStateResponse,
  UpdateSubnetParams,
  UpdateSubnetResponse
} from "./types";

export function* getNetworkSaga(
  action: PayloadAction<GetNetworkParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetNetworkResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/networks/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getNetworkSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getNetworkFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get network data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getNetworksSaga(
  action: PayloadAction<GetNetworksParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetNetworksResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/networks`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getNetworksSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getNetworksFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get networks data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createNetworkSaga(
  action: PayloadAction<CreateNetworkParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the network...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateNetworkResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/networks`,
      data
    );
    yield put(networksActions.createNetworkSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Network has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createNetworkFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create network",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateNetworkSaga(
  action: PayloadAction<UpdateNetworkParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the network...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateNetworkResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/networks/${id}`,
      data
    );
    yield put(networksActions.updateNetworkSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Network has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.updateNetworkFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update network",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteNetworkSaga(
  action: PayloadAction<DeleteNetworkParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete network...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteNetworkResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/networks/${id}`
    );
    yield put(networksActions.deleteNetworkSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete network has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteNetworkFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete network",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getRouterSaga(
  action: PayloadAction<GetRouterParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetRouterResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/routers/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getRouterSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getRouterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get router data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getRoutersSaga(
  action: PayloadAction<GetRoutersParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetRoutersResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/routers`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getRoutersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getRoutersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get routers data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createRouterSaga(
  action: PayloadAction<CreateRouterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the router...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateRouterResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/routers`,
      data
    );
    yield put(networksActions.createRouterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Router has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createRouterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create router",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateRouterSaga(
  action: PayloadAction<UpdateRouterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the router...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateRouterResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/routers/${id}`,
      data
    );
    yield put(networksActions.updateRouterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Router has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.updateRouterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update router",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateRouterStateSaga(
  action: PayloadAction<UpdateRouterStateParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the router state...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateRouterStateResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/routers/${id}/state-up`,
      data
    );
    yield put(networksActions.updateRouterStateSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Router state has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.updateRouterStateFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update router state",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteRouterSaga(
  action: PayloadAction<DeleteRouterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete router...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteRouterResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/routers/${id}`
    );
    yield put(networksActions.deleteRouterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete router has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteRouterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete router",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getInterfacesSaga(
  action: PayloadAction<GetInterfacesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetInterfacesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/interfaces`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getInterfacesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getInterfacesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get interfaces data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getNetworkInterfacesSaga(
  action: PayloadAction<GetNetworkInterfacesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, networkId } = action.payload;
    const response: AxiosResponse<GetNetworkInterfacesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/networks/${networkId}/interfaces`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getNetworkInterfacesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getNetworkInterfacesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get network interfaces data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* editNetworkInterfaceSaga(
  action: PayloadAction<EditNetworkInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Editing the network interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<EditNetworkInterfaceResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/interfaces/${id}`,
      data
    );
    yield put(networksActions.editNetworkInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Network interface has been successfully edited.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.editNetworkInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to edit network interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteNetworkInterfaceSaga(
  action: PayloadAction<DeleteNetworkInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete network interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteNetworkInterfaceResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/interfaces/${id}`
    );
    yield put(networksActions.deleteNetworkInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title:
          "Request to delete network interface has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteNetworkInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete network interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createNetworkInterfaceSaga(
  action: PayloadAction<CreateNetworkInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the network interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateNetworkInterfaceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/interfaces`,
      data
    );
    yield put(networksActions.createNetworkInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Network interface has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createNetworkInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create network interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getRouterInterfacesSaga(
  action: PayloadAction<GetRouterInterfacesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, routerId } = action.payload;
    const response: AxiosResponse<GetRouterInterfacesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/devices/${routerId}/interfaces`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getRouterInterfacesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getRouterInterfacesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get interfaces data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* addRouterInterfaceSaga(
  action: PayloadAction<AddRouterInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Adding the interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, routerId, data } = action.payload;
    const response: AxiosResponse<AddRouterInterfaceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/routers/${routerId}/interfaces`,
      data
    );
    yield put(networksActions.addRouterInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Interface has been successfully added.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.addRouterInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to add interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteRouterInterfaceSaga(
  action: PayloadAction<DeleteRouterInterfaceParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete interface...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, routerId, id, data } = action.payload;
    const response: AxiosResponse<DeleteRouterInterfaceResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/routers/${routerId}/interfaces/${id}`,
      data
    );
    yield put(networksActions.deleteRouterInterfaceSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete interface has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteRouterInterfaceFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete interface",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* editInterfaceFirewallsSaga(
  action: PayloadAction<EditInterfaceFirewallsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Editing the interface's firewalls...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<EditInterfaceFirewallsResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/interfaces/${id}`,
      data
    );
    yield put(networksActions.editInterfaceFirewallsSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Interface's firewalls list has been successfully edited.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.editInterfaceFirewallsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to edit interface's firewall list",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getNetworkLimitsSaga(
  action: PayloadAction<GetNetworkLimitsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetNetworkLimitsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/networks/quotas/detailed`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getNetworkLimitsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getNetworkLimitsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get network limits data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getSubnetSaga(
  action: PayloadAction<GetSubnetParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetSubnetResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/subnets/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getSubnetSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getSubnetFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get subnet data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getSubnetsSaga(
  action: PayloadAction<GetSubnetsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetSubnetsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/subnets`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getSubnetsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getSubnetsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get subnets data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getNetworkSubnetsSaga(
  action: PayloadAction<GetNetworkSubnetsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, networkId } = action.payload;
    const response: AxiosResponse<GetNetworkSubnetsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/networks/${networkId}/subnets`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getNetworkSubnetsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getNetworkSubnetsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get network subnets data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createSubnetSaga(
  action: PayloadAction<CreateSubnetParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the subnet...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateSubnetResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/subnets`,
      data
    );
    yield put(networksActions.createSubnetSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Subnet has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createSubnetFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create subnet",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateSubnetSaga(
  action: PayloadAction<UpdateSubnetParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the subnet...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateSubnetResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/subnets/${id}`,
      data
    );
    yield put(networksActions.updateSubnetSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Subnet has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.updateSubnetFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update subnet",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteSubnetSaga(
  action: PayloadAction<DeleteSubnetParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete subnet...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteSubnetResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/subnets/${id}`
    );
    yield put(networksActions.deleteSubnetSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete subnet has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteSubnetFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete subnet",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFirewallSaga(
  action: PayloadAction<GetFirewallParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetFirewallResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/firewalls/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getFirewallSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getFirewallFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get firewall data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFirewallsSaga(
  action: PayloadAction<GetFirewallsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetFirewallsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/firewalls`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getFirewallsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getFirewallsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get firewalls data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createFirewallSaga(
  action: PayloadAction<CreateFirewallParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the firewall...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateFirewallResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/firewalls`,
      data
    );
    yield put(networksActions.createFirewallSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Firewall has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createFirewallFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create firewall",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateFirewallSaga(
  action: PayloadAction<UpdateFirewallParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the firewall...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateFirewallResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/firewalls/${id}`,
      data
    );
    yield put(networksActions.updateFirewallSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Firewall has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.updateFirewallFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update firewall",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteFirewallSaga(
  action: PayloadAction<DeleteFirewallParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete firewall...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteFirewallResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/firewalls/${id}`
    );
    yield put(networksActions.deleteFirewallSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete firewall has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteFirewallFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete firewall",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFirewallRuleSaga(
  action: PayloadAction<GetFirewallRuleParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetFirewallRuleResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/firewall-rules/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getFirewallRuleSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getFirewallRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get firewall rule data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFirewallRulesSaga(
  action: PayloadAction<GetFirewallRulesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetFirewallRulesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/firewalls/${id}/firewall-rules`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getFirewallRulesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getFirewallRulesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get firewall rules data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createFirewallRuleSaga(
  action: PayloadAction<CreateFirewallRuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the firewall rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateFirewallRuleResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/firewall-rules`,
      data
    );
    yield put(networksActions.createFirewallRuleSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Firewall rule has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createFirewallRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create firewall rule",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteFirewallRuleSaga(
  action: PayloadAction<DeleteFirewallRuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete firewall rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteFirewallRuleResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/firewall-rules/${id}`
    );
    yield put(networksActions.deleteFirewallRuleSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete firewall rule has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteFirewallRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete firewall rule",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* addRouteSaga(
  action: PayloadAction<AddRouteParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Adding the route...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, routerId, data } = action.payload;
    const response: AxiosResponse<AddRouteResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/routers/${routerId}/routes`,
      data
    );
    yield put(networksActions.addRouteSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Route has been successfully added.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.addRouteFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to add route",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteRouteSaga(
  action: PayloadAction<DeleteRouteParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete route...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, routerId, data } = action.payload;
    const routeID = data.destination_cidr.replace(/[^0-9]/g, ""); // generate a compatible id for the route
    const response: AxiosResponse<DeleteRouteResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/routers/${routerId}/routes/${routeID}`,
      data
    );
    yield put(networksActions.deleteRouteSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete route has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteRouteFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete route",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFloatingIPsSaga(
  action: PayloadAction<GetFloatingIPsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetFloatingIPsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-network/method/${projectId}/floatingips`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(networksActions.getFloatingIPsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      networksActions.getFloatingIPsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get floating IPs' data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createFloatingIPSaga(
  action: PayloadAction<CreateFloatingIPParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the floating IP...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateFloatingIPResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-network/method/${projectId}/floatingips`,
      data
    );
    yield put(networksActions.createFloatingIPSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Floating IP has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.createFloatingIPFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create floating IP",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateFloatingIPSaga(
  action: PayloadAction<UpdateFloatingIPParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the floating IP...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateFloatingIPResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-network/method/${projectId}/floatingips/${id}`,
      data
    );
    yield put(networksActions.updateFloatingIPSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Floating IP has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.updateFloatingIPFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update floating IP",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteFloatingIPSaga(
  action: PayloadAction<DeleteFloatingIPParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to release floating IP...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteFloatingIPResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-network/method/${projectId}/floatingips/${id}`
    );
    yield put(networksActions.deleteFloatingIPSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to release floating IP has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      networksActions.deleteFloatingIPFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to release floating IP",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(networksActions.getNetworkRequest, getNetworkSaga),
    takeLeading(networksActions.getNetworksRequest, getNetworksSaga),
    takeEvery(networksActions.createNetworkRequest, createNetworkSaga),
    takeEvery(networksActions.updateNetworkRequest, updateNetworkSaga),
    takeEvery(networksActions.deleteNetworkRequest, deleteNetworkSaga),
    takeLeading(networksActions.getFloatingIPsRequest, getFloatingIPsSaga),
    takeEvery(networksActions.createFloatingIPRequest, createFloatingIPSaga),
    takeEvery(networksActions.updateFloatingIPRequest, updateFloatingIPSaga),
    takeEvery(networksActions.deleteFloatingIPRequest, deleteFloatingIPSaga),
    takeLeading(networksActions.getRouterRequest, getRouterSaga),
    takeLeading(networksActions.getRoutersRequest, getRoutersSaga),
    takeEvery(networksActions.createRouterRequest, createRouterSaga),
    takeEvery(networksActions.updateRouterRequest, updateRouterSaga),
    takeEvery(networksActions.updateRouterStateRequest, updateRouterStateSaga),
    takeEvery(networksActions.deleteRouterRequest, deleteRouterSaga),
    takeEvery(networksActions.getInterfacesRequest, getInterfacesSaga),
    takeEvery(
      networksActions.deleteRouterInterfaceRequest,
      deleteRouterInterfaceSaga
    ),
    takeEvery(
      networksActions.addRouterInterfaceRequest,
      addRouterInterfaceSaga
    ),
    takeEvery(
      networksActions.getRouterInterfacesRequest,
      getRouterInterfacesSaga
    ),
    takeLeading(
      networksActions.getNetworkInterfacesRequest,
      getNetworkInterfacesSaga
    ),
    takeEvery(
      networksActions.editNetworkInterfaceRequest,
      editNetworkInterfaceSaga
    ),
    takeEvery(
      networksActions.createNetworkInterfaceRequest,
      createNetworkInterfaceSaga
    ),
    takeEvery(
      networksActions.deleteNetworkInterfaceRequest,
      deleteNetworkInterfaceSaga
    ),
    takeLeading(networksActions.getNetworkLimitsRequest, getNetworkLimitsSaga),
    takeLeading(networksActions.getSubnetRequest, getSubnetSaga),
    takeLeading(networksActions.getSubnetsRequest, getSubnetsSaga),
    takeLeading(
      networksActions.getNetworkSubnetsRequest,
      getNetworkSubnetsSaga
    ),
    takeEvery(networksActions.createSubnetRequest, createSubnetSaga),
    takeEvery(networksActions.updateSubnetRequest, updateSubnetSaga),
    takeEvery(networksActions.deleteSubnetRequest, deleteSubnetSaga),
    takeLeading(networksActions.getFirewallRequest, getFirewallSaga),
    takeLeading(networksActions.getFirewallsRequest, getFirewallsSaga),
    takeEvery(networksActions.createFirewallRequest, createFirewallSaga),
    takeEvery(networksActions.updateFirewallRequest, updateFirewallSaga),
    takeEvery(networksActions.deleteFirewallRequest, deleteFirewallSaga),
    takeLeading(networksActions.getFirewallRuleRequest, getFirewallRuleSaga),
    takeLeading(networksActions.getFirewallRulesRequest, getFirewallRulesSaga),
    takeEvery(
      networksActions.createFirewallRuleRequest,
      createFirewallRuleSaga
    ),
    takeEvery(
      networksActions.deleteFirewallRuleRequest,
      deleteFirewallRuleSaga
    ),
    takeEvery(networksActions.addRouteRequest, addRouteSaga),
    takeEvery(networksActions.deleteRouteRequest, deleteRouteSaga),
    takeEvery(
      networksActions.editInterfaceFirewallsRequest,
      editInterfaceFirewallsSaga
    )
  ]);
}
